import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Plus, Edit, Trash2, Upload, CheckCircle, XCircle, Users, BookOpen, TrendingUp, FileText } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import ReceiptModal from '../components/ReceiptModal';
import { courses, enrollments, wallet, admin, settings } from '../lib/api';
import { removeToken } from '../lib/auth';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [courseList, setCourseList] = useState([]);
  const [enrollmentList, setEnrollmentList] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [walletConfig, setWalletConfig] = useState({});
  const [upiConfig, setUpiConfig] = useState({});
  const [academySettings, setAcademySettings] = useState({ academy_name: 'Pi German Academy' });
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    level: 'A1',
    title: '',
    description: '',
    price_pi: 0,
    duration: '',
    schedule: '',
  });
  const [walletForm, setWalletForm] = useState({
    wallet_address: '',
    qr_code_base64: '',
  });
  const [upiForm, setUpiForm] = useState({
    upi_id: '',
    qr_code_base64: '',
  });
  const [receiptData, setReceiptData] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const statsRes = await admin.getStats();
      setStats(statsRes.data);

      if (activeTab === 'courses') {
        const coursesRes = await courses.getAll();
        setCourseList(coursesRes.data);
      } else if (activeTab === 'enrollments') {
        const enrollmentsRes = await enrollments.getAll();
        setEnrollmentList(enrollmentsRes.data);
      } else if (activeTab === 'activities') {
        const activitiesRes = await admin.getActivities();
        setActivityLogs(activitiesRes.data);
      } else if (activeTab === 'wallet') {
        const walletRes = await wallet.getConfig();
        setWalletConfig(walletRes.data);
        setWalletForm({
          wallet_address: walletRes.data.wallet_address || '',
          qr_code_base64: walletRes.data.qr_code_base64 || '',
        });
        const upiRes = await wallet.getUpiConfig();
        setUpiConfig(upiRes.data);
        setUpiForm({
          upi_id: upiRes.data.upi_id || '',
          qr_code_base64: upiRes.data.qr_code_base64 || '',
        });
      } else if (activeTab === 'settings') {
        const settingsRes = await settings.getAcademySettings();
        setAcademySettings(settingsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await courses.update(editingCourse.id, courseForm);
        toast.success('Course updated successfully');
      } else {
        await courses.create(courseForm);
        toast.success('Course created successfully');
      }
      setShowCourseModal(false);
      setEditingCourse(null);
      setCourseForm({
        level: 'A1',
        title: '',
        description: '',
        price_pi: 0,
        duration: '',
        schedule: '',
      });
      loadData();
    } catch (error) {
      toast.error('Failed to save course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await courses.delete(courseId);
      toast.success('Course deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      level: course.level,
      title: course.title,
      description: course.description,
      price_pi: course.price_pi,
      duration: course.duration,
      schedule: course.schedule,
    });
    setShowCourseModal(true);
  };

  const handleVerifyPayment = async (enrollmentId, verified) => {
    try {
      await enrollments.verify({ enrollment_id: enrollmentId, verified });
      toast.success(`Payment ${verified ? 'verified' : 'rejected'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleUpdateWallet = async (e) => {
    e.preventDefault();
    try {
      await wallet.updateConfig(walletForm);
      toast.success('Wallet configuration updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update wallet config');
    }
  };

  const handleQRUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setWalletForm({ ...walletForm, qr_code_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateUpi = async (e) => {
    e.preventDefault();
    try {
      await wallet.updateUpiConfig(upiForm);
      toast.success('UPI configuration updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update UPI config');
    }
  };

  const handleUpiQRUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUpiForm({ ...upiForm, qr_code_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateAcademySettings = async (e) => {
    e.preventDefault();
    try {
      await settings.updateAcademySettings({ academy_name: academySettings.academy_name });
      toast.success('Academy name updated successfully');
      loadData();
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update academy settings');
    }
  };

  const handleViewReceipt = async (enrollmentId) => {
    try {
      const res = await enrollments.getReceipt(enrollmentId);
      setReceiptData(res.data);
    } catch (error) {
      toast.error('Failed to load receipt');
    }
  };

  const handleCancelAdmission = async (enrollmentId) => {
    if (!window.confirm('Are you sure you want to cancel this admission? A cancellation receipt will be generated.')) return;
    try {
      await enrollments.cancel({ enrollment_id: enrollmentId });
      toast.success('Admission cancelled successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel admission');
    }
  };

  const handleLogout = () => {
    removeToken();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-gray-50">
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo size="sm" />
            <div className="flex items-center gap-4">
              <button
                onClick={handleLogout}
                className="px-4 py-2 flex items-center gap-2 text-gray-700 hover:text-gray-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-lg text-gray-600">Manage courses, enrollments, and wallet configuration</p>
        </div>

        <div className="flex gap-4 mb-8 overflow-x-auto">
          {['overview', 'courses', 'enrollments', 'activities', 'wallet', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 rounded-lg font-semibold capitalize transition-all ${
                activeTab === tab
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              data-testid={`tab-${tab}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-6 h-6 text-black" />
                    <h3 className="font-semibold text-gray-700">Total Students</h3>
                  </div>
                  <p className="text-4xl font-bold text-gray-900">{stats.total_students || 0}</p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <BookOpen className="w-6 h-6 text-black" />
                    <h3 className="font-semibold text-gray-700">Total Courses</h3>
                  </div>
                  <p className="text-4xl font-bold text-gray-900">{stats.total_courses || 0}</p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                    <h3 className="font-semibold text-gray-700">Verified</h3>
                  </div>
                  <p className="text-4xl font-bold text-gray-900">{stats.verified_enrollments || 0}</p>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-2">
                    <XCircle className="w-6 h-6 text-amber-600" />
                    <h3 className="font-semibold text-gray-700">Pending</h3>
                  </div>
                  <p className="text-4xl font-bold text-gray-900">{stats.pending_enrollments || 0}</p>
                </div>
              </div>
            )}

            {activeTab === 'courses' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Manage Courses</h2>
                  <button
                    onClick={() => setShowCourseModal(true)}
                    className="px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 flex items-center gap-2"
                    data-testid="add-course-btn"
                  >
                    <Plus className="w-5 h-5" />
                    Add Course
                  </button>
                </div>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Level</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Title</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Price (π)</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Duration</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {courseList.map((course) => (
                        <tr key={course.id} data-testid={`course-row-${course.level}`}>
                          <td className="px-6 py-4">
                            <span className="inline-block px-3 py-1 bg-black text-white text-xs font-mono rounded-full">
                              {course.level}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900">{course.title}</td>
                          <td className="px-6 py-4 text-gray-700">{course.price_pi}</td>
                          <td className="px-6 py-4 text-gray-700">{course.duration}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEditCourse(course)}
                                className="p-2 text-gray-600 hover:text-black"
                                data-testid={`edit-course-${course.level}`}
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteCourse(course.id)}
                                className="p-2 text-gray-600 hover:text-red-600"
                                data-testid={`delete-course-${course.level}`}
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'enrollments' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Manage Enrollments</h2>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Student</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Course</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Amount</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Method</th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                        <th className="px-6 py-4 text-right text-sm font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {enrollmentList.map((enrollment) => (
                        <tr key={enrollment.id} data-testid={`enrollment-row-${enrollment.id}`}>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{enrollment.student_name}</p>
                              <p className="text-sm text-gray-500">{enrollment.student_email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{enrollment.course_title}</p>
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded mt-1">
                                {enrollment.course_level}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-700">
                            {enrollment.payment_method === 'upi' ? (
                              <span>INR {(enrollment.amount_inr || enrollment.amount_pi * 356).toLocaleString('en-IN')}</span>
                            ) : (
                              <span>{enrollment.amount_pi} π</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                              enrollment.payment_method === 'upi'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-purple-100 text-purple-700'
                            }`}>
                              {enrollment.payment_method === 'upi' ? 'UPI' : 'Pi'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                enrollment.cancelled
                                  ? 'bg-red-100 text-red-700'
                                  : enrollment.payment_verified
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {enrollment.cancelled ? 'Cancelled' : enrollment.payment_verified ? 'Verified' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {enrollment.cancelled ? (
                              <div className="flex items-center justify-end">
                                <button
                                  onClick={() => handleViewReceipt(enrollment.id)}
                                  className="px-3 py-1.5 bg-red-700 text-white rounded-lg text-sm font-medium hover:bg-red-800 flex items-center gap-1.5"
                                  data-testid={`admin-cancel-receipt-btn-${enrollment.id}`}
                                >
                                  <FileText className="w-4 h-4" />
                                  Cancellation Receipt
                                </button>
                              </div>
                            ) : !enrollment.payment_verified ? (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleVerifyPayment(enrollment.id, true)}
                                  className="p-2 text-green-600 hover:text-green-700"
                                  data-testid={`verify-${enrollment.id}`}
                                >
                                  <CheckCircle className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleVerifyPayment(enrollment.id, false)}
                                  className="p-2 text-red-600 hover:text-red-700"
                                  data-testid={`reject-${enrollment.id}`}
                                >
                                  <XCircle className="w-5 h-5" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleViewReceipt(enrollment.id)}
                                  className="px-3 py-1.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 flex items-center gap-1.5"
                                  data-testid={`admin-receipt-btn-${enrollment.id}`}
                                >
                                  <FileText className="w-4 h-4" />
                                  Receipt
                                </button>
                                <button
                                  onClick={() => handleCancelAdmission(enrollment.id)}
                                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-1.5"
                                  data-testid={`cancel-admission-btn-${enrollment.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'activities' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">User Activity Logs</h2>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Timestamp</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">User</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Activity Type</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {activityLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {new Date(log.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">{log.user_email}</p>
                                <p className="text-xs text-gray-500">{log.user_id}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                log.activity_type === 'login' ? 'bg-blue-100 text-blue-700' :
                                log.activity_type === 'registration' ? 'bg-green-100 text-green-700' :
                                log.activity_type === 'enrollment' ? 'bg-purple-100 text-purple-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {log.activity_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">
                              {log.description}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {activityLogs.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No activity logs yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'wallet' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Pi Wallet Configuration</h2>
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                    <form onSubmit={handleUpdateWallet} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pi Wallet Address
                        </label>
                        <input
                          type="text"
                          value={walletForm.wallet_address}
                          onChange={(e) => setWalletForm({ ...walletForm, wallet_address: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                          placeholder="Enter your Pi wallet address"
                          data-testid="wallet-address-input"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pi Wallet QR Code
                        </label>
                        <div className="flex items-start gap-4">
                          {walletForm.qr_code_base64 && (
                            <img
                              src={walletForm.qr_code_base64}
                              alt="Wallet QR"
                              className="w-48 h-48 rounded-lg border-2 border-gray-200"
                            />
                          )}
                          <div className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleQRUpload}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                              data-testid="qr-upload-input"
                            />
                            <p className="text-sm text-gray-500 mt-2">
                              Upload a QR code image for your Pi wallet
                            </p>
                          </div>
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800"
                        data-testid="save-wallet-btn"
                      >
                        Save Pi Wallet Configuration
                      </button>
                    </form>
                  </div>
                </div>

                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">UPI Payment Configuration</h2>
                  <div className="bg-white rounded-2xl shadow-lg border border-green-200 p-8">
                    <form onSubmit={handleUpdateUpi} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          UPI ID
                        </label>
                        <input
                          type="text"
                          value={upiForm.upi_id}
                          onChange={(e) => setUpiForm({ ...upiForm, upi_id: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                          placeholder="Enter your UPI ID (e.g., name@upi)"
                          data-testid="upi-id-input"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          UPI QR Code
                        </label>
                        <div className="flex items-start gap-4">
                          {upiForm.qr_code_base64 && (
                            <img
                              src={upiForm.qr_code_base64}
                              alt="UPI QR"
                              className="w-48 h-48 rounded-lg border-2 border-green-200"
                              data-testid="upi-qr-preview"
                            />
                          )}
                          <div className="flex-1">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleUpiQRUpload}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                              data-testid="upi-qr-upload-input"
                            />
                            <p className="text-sm text-gray-500 mt-2">
                              Upload a QR code image for UPI payments
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-blue-700 text-sm">
                          <strong>Conversion Rate:</strong> 1 Pi = INR 356. Students who don't have the Pi App can pay via UPI in Indian Rupees.
                        </p>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-3 bg-green-700 text-white rounded-lg font-semibold hover:bg-green-800"
                        data-testid="save-upi-btn"
                      >
                        Save UPI Configuration
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Academy Settings</h2>

                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                  <form onSubmit={handleUpdateAcademySettings} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Academy Name
                      </label>
                      <input
                        type="text"
                        value={academySettings.academy_name}
                        onChange={(e) => setAcademySettings({ ...academySettings, academy_name: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        placeholder="Enter your academy name"
                        data-testid="academy-name-input"
                        required
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        This name will appear in the logo and throughout the application
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-700 text-sm">
                        <strong>Note:</strong> After changing the academy name, the page will reload to update the logo and all references.
                      </p>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                      data-testid="save-academy-name-btn"
                    >
                      Update Academy Name
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCourseModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingCourse ? 'Edit Course' : 'Create New Course'}
              </h2>

              <form onSubmit={handleCreateCourse} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Level</label>
                  <input
                    type="text"
                    value={courseForm.level}
                    onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., A1, A2, B1, B2, C1, C2 or custom"
                    data-testid="course-level-input"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">You can enter any level name (A1-C2 or custom)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., German for Beginners"
                    data-testid="course-title-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    rows="3"
                    placeholder="Course description"
                    data-testid="course-description-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Price (π)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={courseForm.price_pi}
                    onChange={(e) => setCourseForm({ ...courseForm, price_pi: parseFloat(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="100"
                    data-testid="course-price-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration</label>
                  <input
                    type="text"
                    value={courseForm.duration}
                    onChange={(e) => setCourseForm({ ...courseForm, duration: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., 8 weeks"
                    data-testid="course-duration-input"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>
                  <input
                    type="text"
                    value={courseForm.schedule}
                    onChange={(e) => setCourseForm({ ...courseForm, schedule: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., Mon & Wed, 6-8 PM"
                    data-testid="course-schedule-input"
                    required
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCourseModal(false);
                      setEditingCourse(null);
                    }}
                    className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
                    data-testid="cancel-course-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800"
                    data-testid="save-course-btn"
                  >
                    {editingCourse ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {receiptData && (
        <ReceiptModal receipt={receiptData} onClose={() => setReceiptData(null)} />
      )}
    </div>
  );
};

export default AdminDashboard;
